import React, { useState, useEffect } from 'react';
import { quizzesAPI, surveysAPI, gamificationAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Brain, ClipboardList, Trophy, Plus, Users } from 'lucide-react';

export default function AdminEngagement() {
  const [quizzes, setQuizzes] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('quizzes');
  const [showCreate, setShowCreate] = useState(false);
  const [quizForm, setQuizForm] = useState({ title: '', description: '', type: 'knowledge', questions: [] });
  const [surveyForm, setSurveyForm] = useState({ title: '', description: '', questions: [] });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [qRes, sRes, lRes] = await Promise.allSettled([
        quizzesAPI.list({ active_only: false }),
        surveysAPI.list({ active_only: false }),
        gamificationAPI.leaderboard({ limit: 20 }),
      ]);
      if (qRes.status === 'fulfilled') setQuizzes(qRes.value.data.quizzes || []);
      if (sRes.status === 'fulfilled') setSurveys(sRes.value.data.surveys || []);
      if (lRes.status === 'fulfilled') setLeaderboard(lRes.value.data.leaderboard || []);
    } catch {}
    finally { setLoading(false); }
  };

  const createQuiz = async (e) => {
    e.preventDefault();
    try {
      await quizzesAPI.create(quizForm);
      toast.success('Quiz created');
      setShowCreate(false);
      loadAll();
    } catch { toast.error('Failed to create quiz'); }
  };

  const createSurvey = async (e) => {
    e.preventDefault();
    try {
      await surveysAPI.create(surveyForm);
      toast.success('Survey created');
      setShowCreate(false);
      loadAll();
    } catch { toast.error('Failed to create survey'); }
  };

  const tabs = [
    { id: 'quizzes', label: 'Quizzes', icon: Brain },
    { id: 'surveys', label: 'Surveys', icon: ClipboardList },
    { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">Engagement & Gamification</h1>
          <p className="text-sm text-[#5B616E] mt-1">Manage quizzes, surveys, and reader engagement</p>
        </div>
        {activeTab !== 'leaderboard' && (
          <Button onClick={() => setShowCreate(true)} className="bg-[#1E5A8A]">
            <Plus className="w-4 h-4 mr-2" /> Create {activeTab === 'quizzes' ? 'Quiz' : 'Survey'}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-[#E5DFD5] p-5">
          <div className="flex items-center gap-3 mb-2">
            <Brain className="w-5 h-5 text-[#1E5A8A]" />
            <span className="text-xs text-[#5B616E]">Active Quizzes</span>
          </div>
          <p className="text-2xl font-bold text-[#1A1A1A]">{quizzes.filter(q => q.is_active).length}</p>
        </div>
        <div className="bg-white rounded-xl border border-[#E5DFD5] p-5">
          <div className="flex items-center gap-3 mb-2">
            <ClipboardList className="w-5 h-5 text-[#1F7A5A]" />
            <span className="text-xs text-[#5B616E]">Active Surveys</span>
          </div>
          <p className="text-2xl font-bold text-[#1A1A1A]">{surveys.filter(s => s.is_active).length}</p>
        </div>
        <div className="bg-white rounded-xl border border-[#E5DFD5] p-5">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-5 h-5 text-[#B89B5E]" />
            <span className="text-xs text-[#5B616E]">Total Participations</span>
          </div>
          <p className="text-2xl font-bold text-[#1A1A1A]">{quizzes.reduce((a, q) => a + (q.total_attempts || 0), 0) + surveys.reduce((a, s) => a + (s.responses_count || 0), 0)}</p>
        </div>
      </div>

      <div className="flex gap-1 bg-[#F5F0E8] rounded-lg p-1">
        {tabs.map(t => (
          <button key={t.id} onClick={() => { setActiveTab(t.id); setShowCreate(false); }} className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors ${activeTab === t.id ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#5B616E]'}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {showCreate && activeTab === 'quizzes' && (
        <div className="bg-white rounded-xl border border-[#E5DFD5] p-6">
          <form onSubmit={createQuiz} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#5B616E]">Quiz Title</label>
                <input value={quizForm.title} onChange={e => setQuizForm({ ...quizForm, title: e.target.value })} required className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#5B616E]">Type</label>
                <select value={quizForm.type} onChange={e => setQuizForm({ ...quizForm, type: e.target.value })} className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm">
                  <option value="knowledge">Knowledge</option>
                  <option value="personality">Personality</option>
                  <option value="trivia">Trivia</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-[#5B616E]">Description</label>
                <textarea value={quizForm.description} onChange={e => setQuizForm({ ...quizForm, description: e.target.value })} rows={2} className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="bg-[#1E5A8A]">Create Quiz</Button>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      {showCreate && activeTab === 'surveys' && (
        <div className="bg-white rounded-xl border border-[#E5DFD5] p-6">
          <form onSubmit={createSurvey} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#5B616E]">Survey Title</label>
              <input value={surveyForm.title} onChange={e => setSurveyForm({ ...surveyForm, title: e.target.value })} required className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#5B616E]">Description</label>
              <textarea value={surveyForm.description} onChange={e => setSurveyForm({ ...surveyForm, description: e.target.value })} rows={2} className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm" />
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="bg-[#1F7A5A]">Create Survey</Button>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      {activeTab === 'quizzes' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {quizzes.length === 0 ? (
            <div className="col-span-full text-center py-8 text-[#5B616E]">No quizzes yet.</div>
          ) : quizzes.map(q => (
            <div key={q.id} className="bg-white rounded-xl border border-[#E5DFD5] p-5">
              <div className="flex items-center gap-2 mb-2">
                <Brain className="w-4 h-4 text-[#1E5A8A]" />
                <h3 className="font-medium text-sm">{q.title}</h3>
              </div>
              <p className="text-xs text-[#5B616E] mb-3 line-clamp-2">{q.description}</p>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px]">{(q.questions || []).length} questions</Badge>
                <Badge variant="outline" className="text-[10px]">{q.total_attempts || 0} attempts</Badge>
                <Badge className={q.is_active ? 'bg-green-100 text-green-700 text-[10px]' : 'bg-gray-100 text-gray-600 text-[10px]'}>{q.is_active ? 'Active' : 'Inactive'}</Badge>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'surveys' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {surveys.length === 0 ? (
            <div className="col-span-full text-center py-8 text-[#5B616E]">No surveys yet.</div>
          ) : surveys.map(s => (
            <div key={s.id} className="bg-white rounded-xl border border-[#E5DFD5] p-5">
              <div className="flex items-center gap-2 mb-2">
                <ClipboardList className="w-4 h-4 text-[#1F7A5A]" />
                <h3 className="font-medium text-sm">{s.title}</h3>
              </div>
              <p className="text-xs text-[#5B616E] mb-3 line-clamp-2">{s.description}</p>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px]">{s.responses_count || 0} responses</Badge>
                <Badge className={s.is_active ? 'bg-green-100 text-green-700 text-[10px]' : 'bg-gray-100 text-gray-600 text-[10px]'}>{s.is_active ? 'Active' : 'Inactive'}</Badge>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'leaderboard' && (
        <div className="bg-white rounded-xl border border-[#E5DFD5] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F0E8] border-b border-[#E5DFD5]">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E] w-12">#</th>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Reader</th>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Points</th>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Badges</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5DFD5]">
              {leaderboard.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-[#5B616E]">No leaderboard data yet.</td></tr>
              ) : leaderboard.map((l, i) => (
                <tr key={l.id} className="hover:bg-[#FBF7EF]">
                  <td className="px-4 py-3">
                    {i < 3 ? <Trophy className="w-4 h-4" style={{ color: ['#B89B5E', '#9CA3AF', '#CD7F32'][i] }} /> : <span className="text-[#5B616E]">{i + 1}</span>}
                  </td>
                  <td className="px-4 py-3 font-medium">{l.user_name || 'Anonymous'}</td>
                  <td className="px-4 py-3 font-bold text-[#1E5A8A]">{l.points || 0}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(l.badges || []).map((b, j) => (
                        <Badge key={j} variant="outline" className="text-[10px]">{b}</Badge>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
